import React, { useState } from "react";
import PropTypes from "prop-types";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CatalogCategoryPickerDialog from "./CatalogCategoryPickerDialog";
import { CM_TOOLTIPS } from "./utils/tooltipsContent";

export const BULK_CATEGORIZE_MAX_RULES = 3;

export const BULK_CATEGORIZE_TONE_CLASSES = [
  "cm-bulk-cat-tone-0",
  "cm-bulk-cat-tone-1",
  "cm-bulk-cat-tone-2",
];

const BulkCategorizationPanel = ({
  rules,
  onRulesChange,
  onExecuteAll,
  executing = false,
  disabled = false,
  className = "",
  showMatchesOnly = false,
  onShowMatchesOnlyChange,
}) => {
  const [alberoValue, setAlberoValue] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleAddRule = () => {
    const normalizedAlbero = String(alberoValue || "").trim();
    if (!normalizedAlbero || !selectedCategory?.categoryId || disabled || executing) {
      return;
    }
    if (rules.length >= BULK_CATEGORIZE_MAX_RULES) {
      return;
    }
    if (rules.some((rule) => rule.alberoValue === normalizedAlbero)) {
      return;
    }

    onRulesChange([
      ...rules,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        alberoValue: normalizedAlbero,
        categoryId: selectedCategory.categoryId,
        categoryLabel: selectedCategory.categoryLabel,
        colorIndex: rules.length,
      },
    ]);
    setAlberoValue("");
    setSelectedCategory(null);
  };

  const handleRemoveRule = (ruleId) => {
    if (disabled || executing) {
      return;
    }
    onRulesChange(rules.filter((rule) => rule.id !== ruleId));
  };

  const handleCategorySelect = ({ categoryId, pathLabels, alberoCategorie }) => {
    setSelectedCategory({
      categoryId,
      categoryLabel: alberoCategorie || (pathLabels || []).join(";"),
    });
    setPickerOpen(false);
  };

  return (
    <div className={`cm-bulk-categorize-panel${className ? ` ${className}` : ""}`.trim()}>
      <div className="cm-bulk-categorize-form">
        <span className="cm-bulk-categorize-heading" aria-hidden="true">
          <span>BULK</span>
          <span>CAT</span>
        </span>
        <input
          type="text"
          className="cm-input cm-bulk-categorize-albero-field"
          placeholder="Valore albero_categorie"
          value={alberoValue}
          disabled={disabled || executing}
          onChange={(event) => setAlberoValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleAddRule();
            }
          }}
        />
        <button
          type="button"
          className="cm-btn cm-btn-secondary cm-bulk-categorize-pick-btn"
          onClick={() => setPickerOpen(true)}
          disabled={disabled || executing}
          title="Seleziona categoria di destinazione"
        >
          {selectedCategory?.categoryLabel || "Seleziona categoria"}
        </button>
        <button
          type="button"
          className="cm-btn cm-btn-secondary cm-bulk-categorize-add-btn"
          onClick={handleAddRule}
          disabled={disabled || executing || rules.length >= BULK_CATEGORIZE_MAX_RULES}
          title={CM_TOOLTIPS.aggiungiAbbinamentoCategorizzazione}
        >
          Aggiungi abbinamento
        </button>
        <button
          type="button"
          className="cm-btn cm-btn-primary cm-bulk-categorize-run-all-btn"
          onClick={() => onExecuteAll?.(rules)}
          disabled={disabled || executing || rules.length === 0}
          title={CM_TOOLTIPS.eseguiAbbinamentoCategorizzazione}
        >
          {executing ? "Esecuzione..." : "Esegui"}
        </button>
        <button
          type="button"
          className={`cm-btn cm-btn-secondary cm-tool-matches-btn${showMatchesOnly ? " is-active" : ""}`}
          onClick={() => onShowMatchesOnlyChange?.(!showMatchesOnly)}
          disabled={disabled || executing || rules.length === 0 || !onShowMatchesOnlyChange}
          title="Mostra solo righe con albero_categorie che corrisponde alle regole BULK CAT"
        >
          Solo matches
        </button>
      </div>

      {rules.length > 0 && (
        <div className="cm-bulk-categorize-rules">
          {rules.map((rule) => {
            const toneClass = BULK_CATEGORIZE_TONE_CLASSES[rule.colorIndex] || BULK_CATEGORIZE_TONE_CLASSES[0];
            return (
              <div key={rule.id} className={`cm-bulk-categorize-card ${toneClass}`}>
                <span className="cm-bulk-categorize-card-label">
                  &quot;{rule.alberoValue}&quot; → {rule.categoryLabel || rule.categoryId}
                </span>
                <div className="cm-bulk-categorize-card-actions">
                  <IconButton
                    size="small"
                    aria-label="Rimuovi abbinamento categorizzazione"
                    title={CM_TOOLTIPS.rimuoviAbbinamentoCategorizzazione}
                    disabled={disabled || executing}
                    onClick={() => handleRemoveRule(rule.id)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CatalogCategoryPickerDialog
        open={pickerOpen}
        currentCategoryId={selectedCategory?.categoryId ?? null}
        currentPath={selectedCategory?.categoryLabel ?? ""}
        saving={false}
        onClose={() => setPickerOpen(false)}
        onSelect={handleCategorySelect}
      />
    </div>
  );
};

BulkCategorizationPanel.propTypes = {
  rules: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    alberoValue: PropTypes.string.isRequired,
    categoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    categoryLabel: PropTypes.string,
    colorIndex: PropTypes.number,
  })).isRequired,
  onRulesChange: PropTypes.func.isRequired,
  onExecuteAll: PropTypes.func.isRequired,
  executing: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  showMatchesOnly: PropTypes.bool,
  onShowMatchesOnlyChange: PropTypes.func,
};

export default BulkCategorizationPanel;
